
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import {
  ChangePasswordFields,
  ValidationMessages,
} from "@/modules/login/components/forms/change-password/change-password-models";
import IInput from "@/components/input/IInput.vue";
import Message from "@/components/message/Message.vue";
import SubmitButton from "@/components/submit-button/SubmitButton.vue";

export default defineComponent({
  name: "ChangePasswordForm",
  setup() {
    const { t } = useI18n({
      inheritLocale: true,
      useScope: "local",
    });
    return { t };
  },
  components: {
    IInput,
    Message,
    SubmitButton,
  },
  props: {
    policy: {
      type: Object,
    },
    errorMessage: {
      type: String,
    },
  },
  emits: {
    changePassword(password: ChangePasswordFields): boolean {
      return (
        password.old !== "" &&
        password.current !== "" &&
        password.confirm !== "" &&
        password.current !== password.old &&
        password.current === password.confirm
      );
    },
  },
  data() {
    return {
      password: {
        old: "",
        current: "",
        confirm: "",
      } as ChangePasswordFields,
      isSubmitted: false,
    };
  },
  computed: {
    isGeneralError() {
      if (
        this.errorMessage &&
        this.errorMessage !== "login.status_errors.wrong_password" &&
        this.errorMessage !== "login.status_errors.weak_password" &&
        this.errorMessage !== "login.status_errors.password_similar_to_old" &&
        this.errorMessage !== "login.status_errors.passwords_differ" &&
        this.password.old !== "" &&
        this.password.current !== "" &&
        this.password.confirm !== ""
      ) {
        return true;
      }
      return false;
    },
    /** Old Password **/
    oldPasswordFieldErrorMessage() {
      let message = "";
      if (this.isSubmitted) {
        if (this.errorMessage === "login.status_errors.wrong_password") message = this.errorMessage;
        if (this.password.old === "") message = ValidationMessages.RequiredField;
      }
      return message;
    },
    isOldPasswordValid() {
      return this.isGeneralError || this.oldPasswordFieldErrorMessage !== "" ? false : true;
    },
    /** Current Password **/
    currentPasswordFieldErrorMessage(): string {
      let message = "";
      //password dont meet requirement / to weak,
      //can be triggered by both backend and frontend only after submittion
      if (this.isSubmitted) {
        if (!this.isCurrentPasswordMeetRequirement(this.password.current)) {
          message = ValidationMessages.DoesNotMeetPolicy;
        }
        if (this.password.current === "") message = ValidationMessages.RequiredField;
        if (this.errorMessage === "login.status_errors.weak_password") message = this.errorMessage;
      }
      // current password is identical to the old one ,
      // can be triggered by both backend and frontend,
      // front can trigger even before submittion
      if (this.errorMessage === "login.status_errors.password_similar_to_old")
        message = this.errorMessage;
      if (this.password.current !== "" && this.password.current === this.password.old) {
        message = ValidationMessages.IdenticalPasswords;
      }
      return message;
    },
    isCurrentPasswordValid(): boolean {
      if (this.isSubmitted) {
        if (this.password.current === "") return false;
        if (this.isGeneralError) return false;
        for (const value in this.passwordPolicyRequirementObject) {
          if (!this.isCurrentPasswordMeetRequirement(this.password.current)) {
            return false;
          }
        }
        if (this.currentPasswordFieldErrorMessage !== "") {
          return false;
        }
        if (
          this.password.current !== "" &&
          this.password.confirm !== "" &&
          this.password.current !== this.password.confirm
        ) {
          return false;
        }
      }
      if (this.password.current !== "" && this.password.current === this.password.old) {
        return false;
      }
      return true;
    },
    confirmPasswordFieldErrorMessage() {
      let message = "";
      if (this.isSubmitted) {
        // general error appear under confirm field
        if (this.isGeneralError || this.errorMessage === "login.status_errors.passwords_differ")
          message = this.errorMessage;
        if (!this.password.confirm) message = ValidationMessages.RequiredField;
        if (this.password.confirm !== "" && this.password.current !== this.password.confirm) {
          message = ValidationMessages.DoNotMatch;
        }
      }
      return message;
    },
    /** Confirm Password **/
    isConfirmPasswordValid(): boolean {
      if (this.isSubmitted) {
        if (this.password.current === "") return false;
        if (this.isGeneralError) return false;
        for (const value in this.passwordPolicyRequirementObject) {
          if (!this.isCurrentPasswordMeetRequirement(this.password.current)) {
            return false;
          }
        }
        if (this.confirmPasswordFieldErrorMessage !== "") {
          return false;
        }
        if (
          this.password.current !== "" &&
          this.password.confirm !== "" &&
          this.password.current !== this.password.confirm
        ) {
          return false;
        }
      }
      return true;
    },
    passwordPolicyRequirementObject(): Object {
      return {
        minLength: {
          text: `${this.policy["min_length"]}  ${this.t(
            "login.change_password.requirements.min_length"
          )}`,
          regex: new RegExp(`(?=.{${this.policy["min_length"]},})`),
        },
        minUppercase: {
          text: `${this.policy["min_uppercase"]}  ${this.t(
            "login.change_password.requirements.min_upper"
          )}`,
          regex: new RegExp(`(?=.*[A-Z]{${this.policy["min_uppercase"]},})`),
        },
        minDigits: {
          text: `${this.policy["min_digits"]}  ${this.t(
            "login.change_password.requirements.min_digits"
          )}`,
          regex: new RegExp(`(?=.*[0-9]{${this.policy["min_digits"]},})`),
        },
        minSpecial: {
          text: `${this.policy["min_special"]}  ${this.t(
            "login.change_password.requirements.min_special"
          )}`,
          regex: new RegExp(`(?=.*[!@#$%^&*]{${this.policy["min_special"]},})`),
        },
      };
    },
    isSubmissionValid() {
      if (
        (this.isSubmitted &&
          this.isConfirmPasswordValid &&
          this.isCurrentPasswordValid &&
          this.isOldPasswordValid) ||
        !this.isSubmitted
      ) {
        return true;
      }
      return false;
    },
  },
  methods: {
    submitChangePasswordForm(): void {
      this.isSubmitted = true;
      this.$emit("changePassword", {
        ...this.password,
      });
    },
    isCurrentPasswordMeetRequirement(password): boolean {
      for (const value in this.passwordPolicyRequirementObject) {
        if (!this.passwordPolicyRequirementObject[value].regex.test(password)) {
          return false;
        }
      }
      return true;
    },
  },
});
